/**
 * The station services panel (M3 plan R16, §2.9 flow 2): a modal DOM panel titled with the station's name and
 * faction, with one tab per service panel the station offers (`services.list(station)`), mounted one at a time.
 *
 * - While open it holds one `ui.modal` count and one Esc handler (flight input zeroed, no pointer-lock pause; P or
 *   pad Start reach it through the loop's Esc stack) and gives both back on close, relocking the pointer.
 * - Keyboard, mouse and pad drive it through the pure nav model (nav.ts); the pad through M1's `stepPadNav`,
 *   disarmed on open so the press that opened the panel does not also act in it. Pressing a focused button is
 *   `button.click()`, so any panel's plain buttons work, M2's included.
 * - It takes input only while it is the top modal layer and not covered by the pause menu.
 *
 * DOM: the layer is inserted right after `root` (`ctx.overlayRoot`), like R1's dialogs, because M1's overlays
 * rewrite `#overlay` wholesale; services.css hides it while the pause menu shows. UI events only, never per frame.
 */
import type { UiState } from '../../../app/feature'
import { enterModal } from '../../../app/ui'
import { logger } from '../../../core/logger'
import type { StationSite } from '../../../galaxy/layout/types'
import { createPadNav, disarmPadNav, stepPadNav, type PadNavState } from '../../../hud/menu'
import type { PadLike } from '../../../input/playerInput'
import { loadSettingsConfig } from '../../../settings/settings'
import type { PanelContext, StationServicePanel } from '../../ports'
import type { ReachCore } from '../../runtime/types'
import {
  createServicesNav,
  focusNavItem,
  selectNavTab,
  servicesKey,
  servicesNavInput,
  servicesPadInput,
  setNavItems,
  type ServicesInput,
  type ServicesNav,
} from './nav'
import './services.css'

export interface ServicesHostOptions {
  /** The overlay layer the panel sits just above (`ctx.overlayRoot`). */
  root: HTMLElement
  /** The game's modal state; null only outside a game (tests). */
  ui: UiState | null
  core: ReachCore
  station: StationSite
  /** The tabs, in order (`services.list(station)`). */
  panels: readonly StationServicePanel[]
  title: string
  subtitle: string
  /** After the panel has closed and given back its modal slot (the feature autosaves here). */
  onClose?: () => void
}

export interface ServicesPanelHandle {
  readonly station: StationSite
  readonly nav: Readonly<ServicesNav>
  readonly isOpen: boolean
  /** True while open, the top modal layer, and not covered by the pause menu. */
  accepting(): boolean
  input(i: ServicesInput): void
  /** Once per frame while open: reads the pad (edges and repeat) and applies its input. Allocation-free when idle. */
  stepPad(pad: PadLike | null, dt: number): void
  /** Ignores the pad until every nav input is released (on pause, so the resume press does not act here). */
  disarmPad(): void
  close(): void
}

function element<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag)
  e.className = className
  if (text !== undefined) e.textContent = text
  return e
}

/** Enabled buttons under `el`, in document order. */
function collectButtons(el: Element, out: HTMLElement[]): void {
  const kids = el.children
  for (let i = 0; i < kids.length; i++) {
    const k = kids[i] as HTMLElement
    if (k.tagName.toUpperCase() === 'BUTTON') {
      if (!(k as HTMLButtonElement).disabled) out.push(k)
    } else collectButtons(k, out)
  }
}

let panelCount = 0

export function openServicesPanel(o: ServicesHostOptions): ServicesPanelHandle {
  const { core, station, panels, ui } = o
  const nav = createServicesNav(panels.length)
  const id = `reach-services-${++panelCount}`

  const layer = element('div', 'reach-services-layer')
  const dialog = element('div', 'reach-services')
  dialog.setAttribute('role', 'dialog')
  dialog.setAttribute('aria-modal', 'true')
  dialog.setAttribute('aria-labelledby', `${id}-title`)
  const header = element('header', 'rs-header')
  const title = element('h2', 'rs-title', o.title)
  title.id = `${id}-title`
  header.append(title, element('p', 'rs-subtitle', o.subtitle))
  const tabsRow = element('div', 'rs-tabs')
  tabsRow.setAttribute('role', 'tablist')
  const tabButtons = panels.map((p, i) => {
    const b = element('button', 'rs-tab', p.title)
    b.type = 'button'
    b.tabIndex = -1
    b.setAttribute('role', 'tab')
    b.dataset.panel = p.id
    b.addEventListener('click', () => {
      if (open && selectNavTab(nav, i)) {
        mountTab()
        sync()
      }
    })
    tabsRow.append(b)
    return b
  })
  const body = element('div', 'rs-body')
  body.setAttribute('role', 'tabpanel')
  const footer = element('footer', 'rs-footer')
  const closeButton = element('button', 'rs-action rs-close', 'Close')
  closeButton.type = 'button'
  closeButton.tabIndex = -1
  closeButton.addEventListener('click', () => close())
  footer.append(element('span', 'rs-hint', '←/→ tabs · ↑/↓ select · Enter · Esc close'), closeButton)
  dialog.append(header, tabsRow, body, footer)
  layer.append(dialog)
  if (o.root.parentElement) o.root.insertAdjacentElement('afterend', layer)
  else o.root.appendChild(layer)

  let open = true
  let items: HTMLElement[] = [closeButton]
  let unmount: (() => void) | null = null
  const hooked = new WeakSet<HTMLElement>()

  const panelCtx: PanelContext = {
    core,
    station,
    close: (): void => close(),
    refresh: (): void => {
      if (!open) return
      collect()
      sync()
    },
  }

  function collect(): void {
    const found: HTMLElement[] = []
    collectButtons(body, found)
    found.push(closeButton)
    items = found
    for (const b of found) {
      if (hooked.has(b)) continue
      hooked.add(b)
      b.addEventListener('pointerenter', () => {
        if (open && focusNavItem(nav, items.indexOf(b))) sync()
      })
    }
    setNavItems(nav, items.length)
  }

  function mountTab(): void {
    unmount?.()
    unmount = null
    body.replaceChildren()
    const panel = panels[nav.tab] as StationServicePanel | undefined
    if (panel === undefined) body.append(element('p', 'rs-empty', 'This station offers no services.'))
    else {
      try {
        unmount = panel.mount(body, panelCtx)
      } catch (err: unknown) {
        logger.error(`Station service panel "${panel.id}" failed to open`, err)
        body.replaceChildren(element('p', 'rs-empty', 'This service is unavailable.'))
      }
    }
    collect()
  }

  function sync(): void {
    for (let i = 0; i < tabButtons.length; i++) {
      const active = i === nav.tab
      tabButtons[i].classList.toggle('active', active)
      tabButtons[i].setAttribute('aria-selected', active ? 'true' : 'false')
    }
    for (let i = 0; i < items.length; i++) items[i].classList.toggle('focused', i === nav.item)
  }

  const onEscape = (): boolean => {
    close()
    return true
  }
  const leave = ui ? enterModal(ui, onEscape) : null
  const isTop = (): boolean => ui === null || ui.escape[ui.escape.length - 1] === onEscape
  // Hidden by services.css while the pause menu covers it: it must not answer keys meant for the menu.
  const covered = (): boolean => getComputedStyle(layer).visibility === 'hidden'
  const accepting = (): boolean => open && isTop() && !covered()

  function apply(input: ServicesInput): void {
    switch (servicesNavInput(nav, input)) {
      case 'tab':
        mountTab()
        sync()
        break
      case 'focus':
        sync()
        break
      case 'activate':
        items[nav.item]?.click()
        break
      case 'close':
        close()
        break
      case 'none':
        break
    }
  }

  const onKey = (e: KeyboardEvent): void => {
    if (e.defaultPrevented || !accepting()) return
    const input = servicesKey(e.code, e.shiftKey)
    if (input === null) return
    e.preventDefault()
    e.stopPropagation()
    if (e.repeat && (input === 'activate' || input === 'close')) return
    apply(input)
  }

  let padNav: PadNavState | null = null
  try {
    padNav = createPadNav(loadSettingsConfig().menuNav, -1)
    disarmPadNav(padNav)
  } catch (err: unknown) {
    logger.warn('Station services pad navigation unavailable', err)
  }

  function close(): void {
    if (!open) return
    open = false
    window.removeEventListener('keydown', onKey, { capture: true })
    unmount?.()
    unmount = null
    layer.remove()
    leave?.()
    o.onClose?.()
  }

  mountTab()
  sync()
  window.addEventListener('keydown', onKey, { capture: true })

  return {
    station,
    nav,
    get isOpen(): boolean {
      return open
    },
    accepting,
    input(i: ServicesInput): void {
      if (accepting()) apply(i)
    },
    stepPad(pad: PadLike | null, dt: number): void {
      if (!open || padNav === null) return
      const i = stepPadNav(padNav, pad, dt)
      if (i === null || !accepting()) return
      const s = servicesPadInput(i)
      if (s !== null) apply(s)
    },
    disarmPad(): void {
      if (padNav !== null) disarmPadNav(padNav)
    },
    close,
  }
}
